/**
 * Settlement production core — finite, input-funded, timestamp-integrated.
 *
 * There is no global tick. The server evaluates elapsed milliseconds whenever
 * state is read or mutated. Work uses integer fixed-point rates plus a carried
 * remainder, so resolving one long interval or many short intervals produces
 * identical goods and queue progress.
 */
import { INGOT_COLORS, INGOT_METALS, ITEMS, coloredIngotId, type ItemStack } from "../rpg.js";
import type { RoomFunction } from "../homestead/rooms.js";
import { hashSeed } from "../rng.js";
import {
  MEAL_HAPPINESS_CAP_PERMILLE,
  SETTLEMENT_MEAL_POLICIES,
  SETTLEMENT_NIGHT_MS,
  resolveSettlementNightMeal,
  settlementNightIndex,
  settlementRationCount,
  type SettlementMealPolicy,
} from "./meals.js";
import {
  SETTLEMENT_BUILDING_DEFS,
  SETTLEMENT_BUILDING_VARIANTS,
  SETTLEMENT_QUALITY_INDEX,
  SETTLEMENT_QUALITY_TIERS,
  inferredBuildingVariant,
  legacyProfessionSpecialty,
  settlementBuildingCapacity,
  settlementVariantAcceptsProfession,
  type SettlementBuildingFamily,
  type SettlementBuildingLevel,
  type SettlementBuildingVariant,
  type SettlementQuality,
  type SettlementQualityCounts,
  type SettlementQualityPolicy,
  type SettlementQualityStack,
} from "./model.js";

export const SETTLEMENT_STATE_VERSION = 1;
export const SETTLEMENT_STORAGE_BASE = 400;
export const SETTLEMENT_EVENT_MAX = 64;
export const SETTLEMENT_PROGRESSION_MAX = 3;
export const SETTLEMENT_ACCOMPLISHMENT_MAX = 1_000_000_000;
const RATE_SCALE = 1000;

export type SettlementStage = 0 | 1 | 2 | 3;

export const SETTLEMENT_PROFESSIONS = [
  "miner", "farmer", "salvager", "synthesist",
  "metallurgist", "chef", "combiner", "chemist", "fuel-technician",
  "weaponsmith", "fabricator", "engineer", "outfitter", "shipwright",
  "merchant", "broker", "quartermaster", "liaison",
  "steward", "foreman", "coordinator", "recruiter",
] as const;
export const LEGACY_SETTLEMENT_PROFESSIONS =
  ["refiner", "provisioner", "researcher", "harvester", "baker", "armorer"] as const;

/** Rank-2 specialty choices per profession (§3.3/§3.4). A specialty is chosen
 * or changed at promotion; it licenses that profession's direct single-output
 * lines. Professions absent here have no specialty axis yet. */
export const SETTLEMENT_SPECIALTIES: Readonly<Record<string, readonly string[]>> = {
  synthesist: ["red", "blue", "yellow"],
  salvager: ["ferrite", "adamant", "runite"],
};
export const SETTLEMENT_SPECIALTY_TOKENS: ReadonlySet<string> =
  new Set(Object.values(SETTLEMENT_SPECIALTIES).flat());
export type SettlementProfession = (typeof SETTLEMENT_PROFESSIONS)[number]
  | (typeof LEGACY_SETTLEMENT_PROFESSIONS)[number];
export type SettlementRank = 1 | 2 | 3 | 4 | 5;
/** @deprecated Serialized name retained for save compatibility. UI calls this a building level. */
export type FacilityGrade = SettlementBuildingLevel;
export const SETTLEMENT_RESIDENT_TRAITS = [
  "careful", "resourceful", "steady", "curious", "sociable", "bold",
] as const;
export type SettlementResidentTrait = (typeof SETTLEMENT_RESIDENT_TRAITS)[number];

/** @deprecated Buildings no longer multiply speed. Retained for old callers. */
export const FACILITY_RATE_PERMILLE: Readonly<Record<FacilityGrade, number>> = {
  1: 1000, 2: 1000, 3: 1000, 4: 1000, 5: 1000,
};
export const RESIDENT_RATE_PERMILLE: Readonly<Record<SettlementRank, number>> = {
  1: 1000, 2: 1100, 3: 1200, 4: 1300, 5: 1400,
};
export const FACILITY_QUEUE_DEPTH: Readonly<Record<FacilityGrade, number>> = {
  1: 1, 2: 1, 3: 2, 4: 3, 5: 4,
};
export const FACILITY_WORKER_CAP: Readonly<Record<FacilityGrade, number>> = {
  1: 1, 2: 2, 3: 3, 4: 4, 5: 6,
};

/** Cumulative completed-work XP needed to claim each rank. Promotion is an
 * explicit owner action; building level never gates a worker's knowledge. */
export const RESIDENT_RANK_XP: Readonly<Record<SettlementRank, number>> = {
  1: 0, 2: 3, 3: 8, 4: 16, 5: 28,
};

export interface FacilityUpgradeDef {
  toGrade: FacilityGrade;
  name: string;
  costs: readonly ItemStack[];
  /** Deprecated: upgrades now attach their own visible package. */
  requiredFurniture: readonly { id: string; n: number }[];
}

/** The package is installed by the action itself; decoration is never a gate.
 * Levels 2–3 consume an Engineer-crafted package (§5.4) — the upgrade is MADE
 * by a person, then installed by the owner. */
export const FACILITY_UPGRADES: readonly FacilityUpgradeDef[] = [
  {
    toGrade: 2, name: "Established",
    costs: [{ id: "upgrade-package-2", n: 1 }],
    requiredFurniture: [],
  },
  {
    toGrade: 3, name: "Expanded",
    costs: [{ id: "upgrade-package-3", n: 1 }],
    requiredFurniture: [],
  },
  {
    toGrade: 4, name: "Industrial",
    costs: [{ id: "ferriron", n: 24 }, { id: "ore-cryonite", n: 8 }],
    requiredFurniture: [],
  },
  {
    toGrade: 5, name: "Landmark",
    costs: [{ id: "ferriron", n: 48 }, { id: "exotic-crystal", n: 4 }],
    requiredFurniture: [],
  },
] as const;

export function facilityUpgradeAfter(grade: FacilityGrade): FacilityUpgradeDef | null {
  return FACILITY_UPGRADES.find((upgrade) => upgrade.toGrade === grade + 1) ?? null;
}

export interface ProductionRecipe {
  id: string;
  name: string;
  profession: SettlementProfession;
  family: SettlementBuildingFamily;
  variants: readonly SettlementBuildingVariant[];
  facilityFunctions: readonly RoomFunction[];
  minGrade: FacilityGrade;
  minRank: SettlementRank;
  inputs: readonly ItemStack[];
  outputs: readonly ItemStack[];
  /** Common goods can be maintained automatically up to a safe target. */
  targetable: boolean;
  /** XP granted to every assigned worker when the finite batch completes. */
  xp: number;
  /** Nominal Level-1/Rank-1 work duration. */
  workMs: number;
  /** Direct line: the lead must hold this specialty token. */
  specialty?: string;
  /** Balanced line: each batch yields ONE of `outputs`, chosen deterministically
   * as the lowest-stocked candidate, so a full color is never flooded (§3.3). */
  roundRobin?: true;
  /** Combiner transform: output grade uses the exact two-input average
   * equation (§4.3) plus the lead's bounded improvement roll. Such recipes
   * must consume exactly two graded input units. */
  combine?: true;
  /** Rework (§5.6): combine math, but the result never drops below the first
   * input's grade — a beloved piece can only improve. Implies `combine`. */
  rework?: true;
}

/** One readable Generator → Refiner → Crafter chain plus a second food proof. */
const BASE_PRODUCTION_RECIPES: readonly ProductionRecipe[] = [
  {
    id: "mine-ferrite", name: "Extract Ferrite", profession: "miner",
    family: "generator", variants: ["mine"], facilityFunctions: ["mine"],
    minGrade: 1, minRank: 1, inputs: [], outputs: [{ id: "ore-ferrite", n: 1 }],
    targetable: true, xp: 1, workMs: 10 * 60_000,
  },
  {
    id: "grow-wheat", name: "Grow Wheat", profession: "farmer",
    family: "generator", variants: ["farm"], facilityFunctions: ["farm"],
    minGrade: 1, minRank: 1, inputs: [], outputs: [{ id: "wheat", n: 2 }],
    targetable: true, xp: 1, workMs: 12 * 60_000,
  },
  // --- V2 Generators (2026-08-13): renewable Materia and metal lines. A
  // Rank-1 balanced cycle yields one of each; Rank 2+ may target one output
  // directly at double per-color throughput. ---
  {
    id: "synthesize-materia", name: "Balanced Materia Cycle", profession: "synthesist",
    family: "generator", variants: ["harvester"], facilityFunctions: ["harvester"],
    minGrade: 1, minRank: 1, inputs: [],
    outputs: [{ id: "materia-red", n: 1 }, { id: "materia-blue", n: 1 }, { id: "materia-yellow", n: 1 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, roundRobin: true,
  },
  {
    id: "synthesize-materia-red", name: "Synthesize Red Materia", profession: "synthesist",
    family: "generator", variants: ["harvester"], facilityFunctions: ["harvester"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "materia-red", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "red",
  },
  {
    id: "synthesize-materia-blue", name: "Synthesize Blue Materia", profession: "synthesist",
    family: "generator", variants: ["harvester"], facilityFunctions: ["harvester"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "materia-blue", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "blue",
  },
  {
    id: "synthesize-materia-yellow", name: "Synthesize Yellow Materia", profession: "synthesist",
    family: "generator", variants: ["harvester"], facilityFunctions: ["harvester"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "materia-yellow", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "yellow",
  },
  {
    id: "reclaim-metals", name: "Balanced Reclamation", profession: "salvager",
    family: "generator", variants: ["salvage-yard"], facilityFunctions: ["salvage"],
    minGrade: 1, minRank: 1, inputs: [],
    outputs: [{ id: "ore-ferrite", n: 1 }, { id: "ore-adamant", n: 1 }, { id: "ore-runite", n: 1 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, roundRobin: true,
  },
  {
    id: "reclaim-ferrite", name: "Extract Ferrite Seams", profession: "salvager",
    family: "generator", variants: ["salvage-yard"], facilityFunctions: ["salvage"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "ore-ferrite", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "ferrite",
  },
  {
    id: "reclaim-adamant", name: "Extract Adamant Seams", profession: "salvager",
    family: "generator", variants: ["salvage-yard"], facilityFunctions: ["salvage"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "ore-adamant", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "adamant",
  },
  {
    id: "reclaim-runite", name: "Extract Runite Seams", profession: "salvager",
    family: "generator", variants: ["salvage-yard"], facilityFunctions: ["salvage"],
    minGrade: 1, minRank: 2, inputs: [], outputs: [{ id: "ore-runite", n: 2 }],
    targetable: true, xp: 1, workMs: 15 * 60_000, specialty: "runite",
  },
  {
    id: "refine-ferriron", name: "Refine Ferriron", profession: "metallurgist",
    family: "refiner", variants: ["foundry"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ore-ferrite", n: 3 }], outputs: [{ id: "ferriron", n: 2 }], targetable: true,
    xp: 1, workMs: 15 * 60_000,
  },
  {
    id: "stabilize-fuel-cell", name: "Stabilize Fuel Cell", profession: "fuel-technician",
    family: "refiner", variants: ["fuel-refinery"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 2,
    inputs: [{ id: "ore-volatite", n: 3 }, { id: "ore-ferrite", n: 1 }],
    outputs: [{ id: "fuel-cell", n: 1 }], targetable: true, xp: 2, workMs: 30 * 60_000,
  },
  {
    id: "fabricate-light-cells", name: "Fabricate Light Cells", profession: "fabricator",
    family: "crafter", variants: ["workshop"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ferriron", n: 1 }], outputs: [{ id: "ammo-light", n: 120 }],
    targetable: true, xp: 1, workMs: 20 * 60_000,
  },
  // Weaponsmith commissions speak the colored-ingot language (§5): a thermal
  // frame wants Red-Ferrite; dense Adamant drives the heavy slugs.
  {
    id: "forge-field-rifle", name: "Forge Field Rifle", profession: "weaponsmith",
    family: "crafter", variants: ["smithy"], facilityFunctions: ["smithy"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ingot-red-ferrite", n: 2 }, { id: "scrap-alloy", n: 1 }],
    outputs: [{ id: "rifle-mk1", n: 1 }], targetable: false, xp: 2, workMs: 45 * 60_000,
  },
  {
    id: "forge-tempest-minigun", name: "Forge Tempest Rotary Cannon", profession: "weaponsmith",
    family: "crafter", variants: ["smithy"], facilityFunctions: ["smithy"], minGrade: 2, minRank: 2,
    inputs: [{ id: "ingot-yellow-adamant", n: 3 }, { id: "scrap-alloy", n: 3 }],
    outputs: [{ id: "minigun-tempest", n: 1 }], targetable: false, xp: 4, workMs: 75 * 60_000,
  },
  {
    id: "forge-ares-launcher", name: "Forge Ares Rocket Launcher", profession: "weaponsmith",
    family: "crafter", variants: ["smithy"], facilityFunctions: ["smithy"], minGrade: 2, minRank: 2,
    inputs: [{ id: "ingot-orange-runite", n: 3 }, { id: "exotic-crystal", n: 1 }],
    outputs: [{ id: "launcher-ares", n: 1 }], targetable: false, xp: 4, workMs: 90 * 60_000,
  },
  {
    id: "forge-heavy-slugs", name: "Forge Heavy Slugs", profession: "weaponsmith",
    family: "crafter", variants: ["smithy"], facilityFunctions: ["smithy"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ore-adamant", n: 2 }], outputs: [{ id: "ammo-heavy", n: 45 }],
    targetable: true, xp: 1, workMs: 30 * 60_000,
  },
  // Engineer upgrade packages (§5.4): Level 1 builds the Level-2 package,
  // Rank 2 unlocks the Level-3 package. Finite projects, never targets.
  {
    id: "craft-upgrade-package-2", name: "Build Established Works Package", profession: "engineer",
    family: "crafter", variants: ["garage"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "scrap-alloy", n: 6 }, { id: "ore-ferrite", n: 4 }],
    outputs: [{ id: "upgrade-package-2", n: 1 }], targetable: false, xp: 2, workMs: 30 * 60_000,
  },
  {
    id: "craft-upgrade-package-3", name: "Build Expanded Works Package", profession: "engineer",
    family: "crafter", variants: ["garage"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 2,
    inputs: [{ id: "ore-runite", n: 4 }, { id: "ingot-yellow-ferrite", n: 2 }],
    outputs: [{ id: "upgrade-package-3", n: 1 }], targetable: false, xp: 3, workMs: 60 * 60_000,
  },
  // The Kitchen never multiplies calories: one raw ration in, one prepared
  // ration out (§4.2) — cooking adds happiness, not food.
  {
    id: "bake-bread", name: "Bake Bread", profession: "chef",
    family: "refiner", variants: ["bakery"], facilityFunctions: ["kitchen"], minGrade: 1, minRank: 1,
    inputs: [{ id: "wheat", n: 2 }], outputs: [{ id: "bread", n: 2 }],
    targetable: true, xp: 1, workMs: 18 * 60_000,
  },
  {
    id: "cook-hearty-meal", name: "Cook Hearty Meal", profession: "chef",
    family: "refiner", variants: ["bakery"], facilityFunctions: ["kitchen"], minGrade: 1, minRank: 2,
    inputs: [{ id: "wheat", n: 2 }], outputs: [{ id: "hearty-meal", n: 2 }],
    targetable: true, xp: 1, workMs: 24 * 60_000,
  },
  // --- Materia Combiner (§4.3): fixed color equations, always 2 → 1, and the
  // player always chooses the result. ---
  {
    id: "combine-purple", name: "Combine Purple Materia", profession: "combiner",
    family: "refiner", variants: ["combiner"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "materia-red", n: 1 }, { id: "materia-blue", n: 1 }],
    outputs: [{ id: "materia-purple", n: 1 }], targetable: true, xp: 1,
    workMs: 15 * 60_000, combine: true,
  },
  {
    id: "combine-green", name: "Combine Green Materia", profession: "combiner",
    family: "refiner", variants: ["combiner"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "materia-blue", n: 1 }, { id: "materia-yellow", n: 1 }],
    outputs: [{ id: "materia-green", n: 1 }], targetable: true, xp: 1,
    workMs: 15 * 60_000, combine: true,
  },
  {
    id: "combine-orange", name: "Combine Orange Materia", profession: "combiner",
    family: "refiner", variants: ["combiner"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "materia-red", n: 1 }, { id: "materia-yellow", n: 1 }],
    outputs: [{ id: "materia-orange", n: 1 }], targetable: true, xp: 1,
    workMs: 15 * 60_000, combine: true,
  },
  {
    id: "prepare-field-kit", name: "Prepare Field Kit", profession: "chemist",
    family: "refiner", variants: ["processor"], facilityFunctions: ["kitchen", "workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ore-cryonite", n: 1 }, { id: "scrap-alloy", n: 1 }],
    outputs: [{ id: "medkit", n: 1 }], targetable: true, xp: 1, workMs: 20 * 60_000,
  },
  // Rework path (§5.6): a non-critical result is never a permanent mistake.
  // Combine math floored at the piece's own grade — donor materials can only
  // lift it, deterministically.
  {
    id: "rework-field-rifle", name: "Rework Field Rifle", profession: "weaponsmith",
    family: "crafter", variants: ["smithy"], facilityFunctions: ["smithy"], minGrade: 1, minRank: 2,
    inputs: [{ id: "rifle-mk1", n: 1 }, { id: "ingot-red-ferrite", n: 1 }],
    outputs: [{ id: "rifle-mk1", n: 1 }], targetable: false, xp: 2,
    workMs: 30 * 60_000, combine: true, rework: true,
  },
  // The Outfitter (§5.3, merged Armorer+Clothier) owns everything worn.
  {
    id: "tailor-drifter-vest", name: "Tailor Drifter Vest", profession: "outfitter",
    family: "crafter", variants: ["outfit-bench"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ingot-purple-adamant", n: 2 }, { id: "scrap-alloy", n: 1 }],
    outputs: [{ id: "gear-drifter-torso", n: 1 }], targetable: false, xp: 2, workMs: 40 * 60_000,
  },
  {
    id: "tailor-drifter-hood", name: "Tailor Drifter Hood", profession: "outfitter",
    family: "crafter", variants: ["outfit-bench"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ingot-green-adamant", n: 1 }, { id: "scrap-alloy", n: 1 }],
    outputs: [{ id: "gear-drifter-head", n: 1 }], targetable: false, xp: 2, workMs: 30 * 60_000,
  },
  // The Shipwright's Garage (§5.5, launch scope): ground vehicles the game
  // already has. Orange Materia — impact and thrust — drives the frames.
  {
    id: "assemble-hover-bike", name: "Assemble Hover Bike", profession: "shipwright",
    family: "crafter", variants: ["shipyard"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: "ingot-orange-ferrite", n: 3 }, { id: "ferriron", n: 4 }],
    outputs: [{ id: "keycard-hover-bike", n: 1 }], targetable: false, xp: 3, workMs: 90 * 60_000,
  },
  {
    id: "assemble-heavy-hauler", name: "Assemble Heavy Hauler", profession: "shipwright",
    family: "crafter", variants: ["shipyard"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 2,
    inputs: [{ id: "ingot-orange-adamant", n: 3 }, { id: "ferriron", n: 6 }],
    outputs: [{ id: "keycard-hauler-speeder", n: 1 }], targetable: false, xp: 4, workMs: 120 * 60_000,
  },
  {
    id: "engineer-resonance-amp", name: "Build Resonance Amp", profession: "engineer",
    family: "crafter", variants: ["garage"], facilityFunctions: ["workshop"], minGrade: 1, minRank: 3,
    inputs: [{ id: "ingot-blue-runite", n: 2 }, { id: "ferriron", n: 4 }],
    outputs: [{ id: "module-mining", n: 1 }], targetable: false, xp: 3, workMs: 90 * 60_000,
  },
] as const;

const titleWord = (word: string): string => word[0]!.toUpperCase() + word.slice(1);

/** Ingot infusion (§4.3): 1 raw metal + 1 Materia → 1 colored ingot. All
 * eighteen identities are generated so recipes cannot drift from item ids. */
const INFUSION_RECIPES: readonly ProductionRecipe[] = INGOT_METALS.flatMap((metal) =>
  INGOT_COLORS.map((color): ProductionRecipe => ({
    id: `infuse-${color}-${metal}`,
    name: `Infuse ${titleWord(color)}-${titleWord(metal)} Ingot`,
    profession: "combiner", family: "refiner", variants: ["combiner"],
    facilityFunctions: ["workshop"], minGrade: 1, minRank: 1,
    inputs: [{ id: `ore-${metal}`, n: 1 }, { id: `materia-${color}`, n: 1 }],
    outputs: [{ id: coloredIngotId(color, metal), n: 1 }],
    targetable: true, xp: 1, workMs: 20 * 60_000, combine: true,
  })));

export const PRODUCTION_RECIPES: readonly ProductionRecipe[] =
  [...BASE_PRODUCTION_RECIPES, ...INFUSION_RECIPES];

export const PRODUCTION_RECIPE_BY_ID: ReadonlyMap<string, ProductionRecipe> =
  new Map(PRODUCTION_RECIPES.map((recipe) => [recipe.id, recipe]));

export interface SettlementResidentV1 {
  id: string;
  name: string;
  profession: SettlementProfession | null;
  rank: SettlementRank;
  xp: number;
  homePid: number | null;
  workplacePid: number | null;
  /** Stable facility ownership, independent from the replaceable physical
   * workstation pid. Optional only for pre-v47 resident records. */
  facilityId?: string;
  /** Optional only for persisted pre-recruitment residents. New residents
   * always snapshot one deterministic identity trait. */
  trait?: SettlementResidentTrait;
  /** Rank-2+ specialty token (SETTLEMENT_SPECIALTIES). Chosen or changed at
   * promotion; a strength, not a trap. */
  specialty?: string;
  /** Night index this resident last ate for. They work day N only when fed at
   * night N; absent on pre-meal saves and new arrivals, who count as fed. */
  fedForDay?: number;
  /** Personal next-day work-speed bonus from last night's meal. */
  mealHappinessPermille?: number;
}

/** Owner-only candidate. Geometry bindings are proposals and are revalidated
 * transactionally when accepted; candidates never enter the public projection. */
export interface SettlementApplicantV1 {
  id: string;
  name: string;
  profession: SettlementProfession;
  trait: SettlementResidentTrait;
  homePid: number;
  workplacePid: number;
  facilityId: string;
  facilityAnchorPid: number;
  roomFunction: RoomFunction;
  expiresAt: number;
  /** Bounded server-derived alternatives for this same identity/profession.
   * The top-level binding remains the default for pre-v46 clients/saves. */
  placements?: SettlementApplicantPlacementV1[];
}

export interface SettlementApplicantPlacementV1 {
  homePid: number;
  workplacePid: number;
  facilityId: string;
  facilityAnchorPid: number;
  roomFunction: RoomFunction;
}

export type CommissionPauseReason =
  "FACILITY_INVALID" | "WORKER_UNAVAILABLE" | "WORKER_UNFED" | "STORAGE_FULL";

export interface SettlementCommissionV1 {
  id: string;
  recipeId: string;
  workerIds: string[];
  /** Inputs are removed from storage when queued and remain declared here. */
  escrowedInputs: ItemStack[];
  /** Exact qualities reserved at start; absent only on legacy jobs. */
  qualityInputs?: SettlementQualityStack[];
  /** Deterministic result fixed at queue time, so requeueing cannot reroll. */
  outputQuality?: SettlementQuality;
  workRequiredMs: number;
  workDoneMs: number;
  /** Per-lane fixed-point carry. Optional only for serialized pre-parallel jobs. */
  rateRemainder?: number;
  pauseReason?: CommissionPauseReason;
}

export interface SettlementFacilityV1 {
  id: string;
  anchorPid: number;
  /** @deprecated Pre-six-family license. New buildings are capability-neutral. */
  profession?: SettlementProfession;
  family?: SettlementBuildingFamily;
  variant?: SettlementBuildingVariant;
  roomFunction: RoomFunction;
  grade: FacilityGrade;
  queue: SettlementCommissionV1[];
  /** Last server timestamp integrated for this queue. */
  lastEvaluatedAt: number;
  /** Sub-millisecond fixed-point work carried across evaluations. */
  rateRemainder: number;
  nextCommission: number;
  /** Common-good order. It safely plateaus at target, inputs, or capacity. */
  target?: SettlementTargetOrderV1;
}

export interface SettlementTargetOrderV1 {
  recipeId: string;
  targetQuantity: number;
  workerIds: string[];
  qualityPolicy: SettlementQualityPolicy;
}

export interface SettlementProductionEventV1 {
  id: string;
  kind: "commission-complete";
  facilityId: string;
  recipeId: string;
  outputs: SettlementQualityStack[];
}

export interface SettlementStateV1 {
  v: 1;
  beaconId: string;
  residents: SettlementResidentV1[];
  facilities: SettlementFacilityV1[];
  storage: Record<string, number>;
  /** Counts by Low → Pristine. Aggregate `storage` remains the inventory seam. */
  qualityStorage?: Record<string, SettlementQualityCounts>;
  storageCapacity: number;
  events: SettlementProductionEventV1[];
  /** Six-hour deterministic applicant window. These fields are normalized on
   * read so pre-v44 settlement documents remain valid. */
  applicantEpoch: number;
  applicants: SettlementApplicantV1[];
  /** Settlement growth is an explicit owner action. Both fields are
   * normalized on read so pre-v45 documents remain valid. */
  stage: SettlementStage;
  completedCommissions: number;
  /** Last resolved fixed-cadence night. Normalized on read for pre-meal saves. */
  lastMealNight?: number;
  /** One town-wide meal policy; there are no per-resident assignments. */
  mealPolicy?: SettlementMealPolicy;
  /** One-copy storefront receipts for the current rotation (§5). Offer ids
   * embed their epoch; stale receipts are pruned on purchase. */
  storefrontPurchases?: string[];
  /** Deposited Bank credits (§6.1). Interest accrues lazily, capped by the
   * Bank building's level and the seven-day offline accrual rule. */
  bank?: SettlementBankV1;
  /** Pinned Guild orders with their escrowed goods (§6.2). Pinned orders
   * never silently expire; abandoning refunds the exact escrow grades. */
  guildOrders?: SettlementGuildOrderV1[];
  updatedAt: number;
}

export interface SettlementBankV1 {
  credits: number;
  /** Last 24h-day index interest was applied for. */
  lastInterestDay: number;
}

export interface SettlementGuildOrderV1 {
  id: string;
  itemId: string;
  minQuality: SettlementQuality;
  quantity: number;
  premiumPermille: number;
  contributed: number;
  escrow: SettlementQualityStack[];
}

export const BANK_DAILY_RATE_PERMYRIAD = 5;
export const BANK_ACCRUAL_CAP_DAYS = 7;
export const BANK_CREDITS_MAX = 100_000_000;
export const BANK_DAY_MS = 24 * 3_600_000;
/** Interest-bearing principal cap by Bank building level (§6.1). */
export const BANK_INTEREST_CAPS: Readonly<Record<1 | 2 | 3, number>> = {
  1: 10_000, 2: 50_000, 3: 250_000,
};

export function settlementBankLevel(state: SettlementStateV1): 0 | 1 | 2 | 3 {
  let level = 0;
  for (const facility of state.facilities) {
    if (settlementFacilityVariant(facility) === "bank") level = Math.max(level, Math.min(3, facility.grade));
  }
  return level as 0 | 1 | 2 | 3;
}

/** Lazy daily simple interest from each day's opening principal, capped by
 * level and by seven offline days (§6.1 — the cap is an intentional break in
 * split invariance: absence beyond a week stops compounding by design). */
function resolveBankInterest(state: SettlementStateV1, nowMs: number): void {
  if (!state.bank) return;
  const day = Math.floor(Math.max(0, nowMs) / BANK_DAY_MS);
  if (day <= state.bank.lastInterestDay) return;
  const level = settlementBankLevel(state);
  if (level >= 1) {
    const cap = BANK_INTEREST_CAPS[level as 1 | 2 | 3];
    const days = Math.min(BANK_ACCRUAL_CAP_DAYS, day - state.bank.lastInterestDay);
    for (let i = 0; i < days; i++) {
      state.bank.credits = Math.min(BANK_CREDITS_MAX, state.bank.credits
        + Math.floor(Math.min(state.bank.credits, cap) * BANK_DAILY_RATE_PERMYRIAD / 10_000));
    }
  }
  state.bank.lastInterestDay = day;
}

export interface ProductionContext {
  /** When supplied, facilities absent from the set pause for invalid geometry. */
  activeFacilityIds?: ReadonlySet<string>;
  /** Derived physical stations licensed for each facility. Residents bind to a
   * station pid, while the facility keeps one stable function-anchor identity. */
  facilityWorkstationPids?: ReadonlyMap<string, ReadonlySet<number>>;
}

export interface QueueCommissionResult {
  ok: boolean;
  state: SettlementStateV1;
  commissionId?: string;
  err?: string;
}

function stacksCopy(stacks: readonly ItemStack[]): ItemStack[] {
  return stacks.map((stack) => ({ id: stack.id, n: stack.n }));
}

function qualityStacksCopy(stacks: readonly SettlementQualityStack[]): SettlementQualityStack[] {
  return stacks.map((stack) => ({ ...stack }));
}

export function settlementFacilityVariant(facility: SettlementFacilityV1): SettlementBuildingVariant {
  return facility.variant ?? inferredBuildingVariant(facility.roomFunction, facility.profession ?? null);
}

export function settlementFacilityFamily(facility: SettlementFacilityV1): SettlementBuildingFamily {
  return facility.family ?? SETTLEMENT_BUILDING_DEFS[settlementFacilityVariant(facility)].family;
}

export function settlementFacilityCapacity(facility: SettlementFacilityV1) {
  return settlementBuildingCapacity(settlementFacilityFamily(facility), facility.grade);
}

export function cloneSettlementState(state: SettlementStateV1): SettlementStateV1 {
  return {
    ...state,
    residents: state.residents.map((resident) => ({
      ...resident,
      profession: legacyProfessionSpecialty(resident.profession) as SettlementProfession | null,
    })),
    facilities: state.facilities.map((facility) => ({
      ...facility,
      family: settlementFacilityFamily(facility),
      variant: settlementFacilityVariant(facility),
      queue: facility.queue.map((job) => ({
        ...job,
        workerIds: [...job.workerIds],
        escrowedInputs: stacksCopy(job.escrowedInputs),
        ...(job.qualityInputs ? { qualityInputs: qualityStacksCopy(job.qualityInputs) } : {}),
      })),
      ...(facility.target ? { target: { ...facility.target, workerIds: [...facility.target.workerIds] } } : {}),
    })),
    storage: { ...state.storage },
    ...(state.storefrontPurchases ? { storefrontPurchases: [...state.storefrontPurchases] } : {}),
    ...(state.bank ? { bank: { ...state.bank } } : {}),
    ...(state.guildOrders ? { guildOrders: state.guildOrders.map((order) => ({
      ...order, escrow: qualityStacksCopy(order.escrow) })) } : {}),
    qualityStorage: Object.fromEntries(Object.entries(state.qualityStorage ?? {}).map(
      ([id, counts]) => [id, [...counts] as SettlementQualityCounts])),
    events: state.events.map((event) => ({ ...event, outputs: qualityStacksCopy(event.outputs) })),
    applicants: state.applicants.map((applicant) => ({
      ...applicant,
      ...(applicant.placements ? { placements: applicant.placements.map((placement) => ({ ...placement })) } : {}),
    })),
  };
}

export function emptySettlementState(beaconId: string, nowMs: number): SettlementStateV1 {
  return {
    v: SETTLEMENT_STATE_VERSION,
    beaconId,
    residents: [],
    facilities: [],
    storage: {},
    qualityStorage: {},
    storageCapacity: SETTLEMENT_STORAGE_BASE,
    events: [],
    applicantEpoch: 0,
    applicants: [],
    stage: 0,
    completedCommissions: 0,
    lastMealNight: settlementNightIndex(Number.isFinite(nowMs) ? nowMs : 0),
    mealPolicy: "balanced",
    updatedAt: nowMs,
  };
}

export function settlementStorageUsed(storage: Readonly<Record<string, number>>): number {
  let total = 0;
  for (const n of Object.values(storage)) if (Number.isInteger(n) && n > 0) total += n;
  return total;
}

function hasStacks(storage: Readonly<Record<string, number>>, stacks: readonly ItemStack[]): boolean {
  return stacks.every((stack) => (storage[stack.id] ?? 0) >= stack.n);
}

function qualityCounts(state: SettlementStateV1, id: string): SettlementQualityCounts {
  const existing = state.qualityStorage?.[id];
  if (existing) return existing;
  const counts: SettlementQualityCounts = [0, state.storage[id] ?? 0, 0, 0, 0];
  (state.qualityStorage ??= {})[id] = counts;
  return counts;
}

/** Grouped owner UI/read model. Old ungraded goods migrate to Medium. */
export function settlementQualityBreakdown(state: SettlementStateV1,
  id: string): Readonly<SettlementQualityCounts> {
  return [...qualityCounts(state, id)] as SettlementQualityCounts;
}

function takeQuality(state: SettlementStateV1, stack: ItemStack,
  policy: SettlementQualityPolicy = "lowest-first"): SettlementQualityStack[] | null {
  if ((state.storage[stack.id] ?? 0) < stack.n) return null;
  const counts = qualityCounts(state, stack.id);
  const indices = policy === "at-least-high" ? [2, 3, 4] : [0, 1, 2, 3, 4];
  let left = stack.n;
  const taken: SettlementQualityStack[] = [];
  for (const index of indices) {
    if (left <= 0) break;
    const n = Math.min(left, counts[index] ?? 0);
    if (n <= 0) continue;
    counts[index] -= n;
    taken.push({ id: stack.id, n, quality: (["low", "medium", "high", "premium", "pristine"] as const)[index]! });
    left -= n;
  }
  if (left > 0) {
    for (const row of taken) counts[SETTLEMENT_QUALITY_INDEX[row.quality]] += row.n;
    return null;
  }
  const next = (state.storage[stack.id] ?? 0) - stack.n;
  if (next > 0) state.storage[stack.id] = next;
  else delete state.storage[stack.id];
  return taken;
}

export function addSettlementQualityGoods(state: SettlementStateV1, id: string,
  n: number, quality: SettlementQuality): void {
  if (n <= 0) return;
  const counts = qualityCounts(state, id);
  state.storage[id] = (state.storage[id] ?? 0) + n;
  counts[SETTLEMENT_QUALITY_INDEX[quality]] += n;
}

export function removeSettlementGoods(state: SettlementStateV1, id: string, n: number,
  policy: SettlementQualityPolicy = "lowest-first"): SettlementQualityStack[] | null {
  return takeQuality(state, { id, n }, policy);
}

/** How many stored units meet a minimum grade (Guild orders, §6.2). */
export function settlementCountAtLeast(state: SettlementStateV1, id: string,
  minQuality: SettlementQuality): number {
  const counts = state.qualityStorage?.[id];
  const min = SETTLEMENT_QUALITY_INDEX[minQuality];
  if (!counts) return min <= 1 ? state.storage[id] ?? 0 : 0;
  return counts.reduce((sum, n, index) => index >= min ? sum + n : sum, 0);
}

/** Remove `n` units of at least `minQuality`, lowest qualifying grade first.
 * Null and no change when short. */
export function removeSettlementGoodsAtLeast(state: SettlementStateV1, id: string, n: number,
  minQuality: SettlementQuality): SettlementQualityStack[] | null {
  if (settlementCountAtLeast(state, id, minQuality) < n) return null;
  const counts = qualityCounts(state, id);
  const min = SETTLEMENT_QUALITY_INDEX[minQuality];
  const taken: SettlementQualityStack[] = [];
  let left = n;
  for (let index = min; index < 5 && left > 0; index++) {
    const take = Math.min(left, counts[index] ?? 0);
    if (take <= 0) continue;
    counts[index] -= take;
    taken.push({ id, n: take, quality: SETTLEMENT_QUALITY_TIERS[index]! });
    left -= take;
  }
  const next = (state.storage[id] ?? 0) - n;
  if (next > 0) state.storage[id] = next;
  else delete state.storage[id];
  return taken;
}

function outputsFit(state: SettlementStateV1, stacks: readonly ItemStack[]): boolean {
  let add = 0;
  for (const stack of stacks) add += stack.n;
  return settlementStorageUsed(state.storage) + add <= state.storageCapacity;
}

/** Spec §5.1 crafter critical chance by lead Rank: a critical improves the
 * result one grade past the material band. Deterministic per project, capped
 * Q5, and never turns a valid project into a failure. */
export const CRAFTER_CRIT_PCT: Readonly<Record<SettlementRank, number>> = {
  1: 2, 2: 5, 3: 10, 4: 16, 5: 25,
};

/** Spec §3.2 rank bands for Generator output: mostly the rank's home grade
 * with occasional neighbours. Percent weights Q1..Q5; rows sum to 100. */
export const GENERATOR_GRADE_WEIGHTS: Readonly<Record<SettlementRank, readonly number[]>> = {
  1: [80, 20, 0, 0, 0],
  2: [15, 70, 15, 0, 0],
  3: [0, 15, 70, 15, 0],
  4: [0, 0, 15, 70, 15],
  5: [0, 0, 0, 40, 60],
};

function outputQuality(state: SettlementStateV1, job: SettlementCommissionV1,
  recipe: ProductionRecipe): SettlementQuality {
  if (job.outputQuality) return job.outputQuality;
  const lead = state.residents.find((resident) => resident.id === job.workerIds[0]);
  const rank = lead?.rank ?? 1;
  const tiers = ["low", "medium", "high", "premium", "pristine"] as const;
  const deterministic = hashSeed(`${job.id}:quality:${rank}`) % 100;
  if (recipe.combine) {
    // §4.3 exact equation in Q numbers: base = floor((a+b)/2); an odd sum
    // grants a deterministic half-step round-up keyed to the work unit, so
    // canceling and requeueing can never reroll it.
    const units = (job.qualityInputs ?? []).flatMap((row) =>
      Array.from({ length: row.n }, () => SETTLEMENT_QUALITY_INDEX[row.quality] + 1));
    const a = units[0] ?? 2, b = units[1] ?? a;
    let q = Math.floor((a + b) / 2);
    if ((a + b) % 2 === 1 && hashSeed(`${job.id}:combine:roundup`) % 2 === 0) q += 1;
    // The lead Combiner's bounded improvement roll: +1 grade at 5/10/15/20%
    // for Ranks 2–5, capped at Q5 (§4.3).
    const chancePct = [0, 5, 10, 15, 20][rank - 1]!;
    if (hashSeed(`${job.id}:combine:improve`) % 100 < chancePct) q += 1;
    // Rework (§5.6): the piece being reworked (first input) can only improve.
    if (recipe.rework) q = Math.max(q, a);
    return tiers[Math.max(0, Math.min(4, q - 1))]!;
  }
  if (recipe.family === "generator") {
    let roll = deterministic;
    for (let index = 0; index < tiers.length; index++) {
      roll -= GENERATOR_GRADE_WEIGHTS[rank][index]!;
      if (roll < 0) return tiers[index]!;
    }
    return "pristine";
  }
  const inputFloor = job.qualityInputs?.length
    ? Math.min(...job.qualityInputs.map((stack) => SETTLEMENT_QUALITY_INDEX[stack.quality])) : 1;
  if (recipe.family === "crafter") {
    // §5.1: materials set the normal band; the lead may critically improve the
    // result by one grade — deterministic per project, never rerollable.
    const crit = hashSeed(`${job.id}:craft:crit`) % 100 < CRAFTER_CRIT_PCT[rank];
    return tiers[Math.min(4, inputFloor + (crit ? 1 : 0))]!;
  }
  const ceiling = Math.min(4, inputFloor + Math.floor(rank / 2));
  const floor = Math.max(0, Math.min(inputFloor, ceiling));
  const span = Math.max(1, ceiling - floor + 1);
  const biased = Math.floor(deterministic * span / 100);
  return tiers[Math.min(4, floor + biased)]!;
}

/** What one completed batch actually deposits. Balanced round-robin lines
 * yield the lowest-stocked candidate (ties resolve in recipe order), chosen
 * from deterministic state at the completion boundary. */
function effectiveOutputs(state: SettlementStateV1, recipe: ProductionRecipe): ItemStack[] {
  if (!recipe.roundRobin) return stacksCopy(recipe.outputs);
  let pick = recipe.outputs[0]!;
  for (const candidate of recipe.outputs) {
    if ((state.storage[candidate.id] ?? 0) < (state.storage[pick.id] ?? 0)) pick = candidate;
  }
  return [{ ...pick }];
}

function addOutputs(state: SettlementStateV1, job: SettlementCommissionV1,
  recipe: ProductionRecipe, outputs: readonly ItemStack[]): SettlementQualityStack[] {
  const quality = outputQuality(state, job, recipe);
  job.outputQuality = quality;
  return outputs.map((stack) => {
    addSettlementQualityGoods(state, stack.id, stack.n, quality);
    return { ...stack, quality };
  });
}

function awardCommissionXp(state: SettlementStateV1, job: SettlementCommissionV1,
  recipe: ProductionRecipe): void {
  for (const id of new Set(job.workerIds)) {
    const resident = state.residents.find((candidate) => candidate.id === id);
    if (resident) resident.xp = Math.min(1_000_000_000, resident.xp + recipe.xp);
  }
}

function recipeFitsBuilding(recipe: ProductionRecipe, facility: SettlementFacilityV1): boolean {
  return recipe.family === settlementFacilityFamily(facility)
    && recipe.variants.includes(settlementFacilityVariant(facility))
    && recipe.facilityFunctions.includes(facility.roomFunction);
}

function reserveRecipeInputs(state: SettlementStateV1, recipe: ProductionRecipe,
  policy: SettlementQualityPolicy): SettlementQualityStack[] | null {
  if (!hasStacks(state.storage, recipe.inputs)) return null;
  const reserved: SettlementQualityStack[] = [];
  for (const input of recipe.inputs) {
    const rows = takeQuality(state, input, policy);
    if (!rows) {
      for (const row of reserved) addSettlementQualityGoods(state, row.id, row.n, row.quality);
      return null;
    }
    reserved.push(...rows);
  }
  return reserved;
}

function appendCommission(state: SettlementStateV1, facility: SettlementFacilityV1,
  recipe: ProductionRecipe, workerIds: string[], policy: SettlementQualityPolicy): string | null {
  const qualityInputs = reserveRecipeInputs(state, recipe, policy);
  if (!qualityInputs && recipe.inputs.length > 0) return null;
  const id = `commission:${facility.id}:${facility.nextCommission++}`;
  facility.queue.push({
    id, recipeId: recipe.id, workerIds: [...workerIds],
    escrowedInputs: stacksCopy(recipe.inputs), qualityInputs: qualityInputs ?? [],
    workRequiredMs: recipe.workMs, workDoneMs: 0,
  });
  return id;
}

function targetNeedsWork(state: SettlementStateV1, facility: SettlementFacilityV1,
  recipe: ProductionRecipe): boolean {
  const target = facility.target;
  if (!target || target.recipeId !== recipe.id) return false;
  const output = recipe.outputs[0];
  if (!output) return false;
  if (recipe.roundRobin) {
    // A balanced target means "keep every candidate at N": work continues
    // while the lowest-stocked candidate is below it.
    const queued = facility.queue.filter((job) => job.recipeId === recipe.id).length;
    const low = Math.min(...recipe.outputs.map((stack) => state.storage[stack.id] ?? 0));
    return low + queued + 1 <= target.targetQuantity;
  }
  const queued = facility.queue.filter((job) => job.recipeId === recipe.id).length * output.n;
  return (state.storage[output.id] ?? 0) + queued + output.n <= target.targetQuantity;
}

function startTargetIfPossible(state: SettlementStateV1, facility: SettlementFacilityV1,
  context: ProductionContext): boolean {
  if (!facility.target || facility.queue.length >= settlementFacilityCapacity(facility).parallelJobs) return false;
  const recipe = PRODUCTION_RECIPE_BY_ID.get(facility.target.recipeId);
  if (!recipe?.targetable || !recipeFitsBuilding(recipe, facility) || !targetNeedsWork(state, facility, recipe)) return false;
  const workers = facility.target.workerIds.map((id) => state.residents.find((resident) => resident.id === id));
  if (workers.length < 1 || workers.length > facilityWorkerCap(facility, context)
    || workers.some((worker) => !worker || !workerUsesFacility(worker, facility, context))
    || legacyProfessionSpecialty(workers[0]!.profession) !== recipe.profession
    || workers[0]!.rank < recipe.minRank
    || !leadHasSpecialty(workers[0]!, recipe)
    || !residentFed(state, workers[0]!)) return false;
  return appendCommission(state, facility, recipe, facility.target.workerIds,
    facility.target.qualityPolicy) !== null;
}

/** Direct single-output lines require the lead's chosen specialty (§3.3). */
function leadHasSpecialty(worker: SettlementResidentV1, recipe: ProductionRecipe): boolean {
  return recipe.specialty === undefined || worker.specialty === recipe.specialty;
}

/** Fed status for the current workday segment. Pre-meal saves and brand-new
 * residents have no meal history and count as fed until their first night. */
function residentFed(state: SettlementStateV1, worker: SettlementResidentV1): boolean {
  return (worker.fedForDay ?? state.lastMealNight ?? 0) >= (state.lastMealNight ?? 0);
}

function happinessScale(worker: SettlementResidentV1): number {
  return 1000 + Math.max(0, Math.min(MEAL_HAPPINESS_CAP_PERMILLE, worker.mealHappinessPermille ?? 0));
}

function facilityWorkerCap(facility: SettlementFacilityV1, context: ProductionContext): number {
  const physical = context.facilityWorkstationPids?.get(facility.id);
  const cap = settlementFacilityCapacity(facility).workerSlots;
  return Math.min(cap, physical?.size ?? cap);
}

function workerUsesFacility(worker: SettlementResidentV1, facility: SettlementFacilityV1,
  context: ProductionContext): boolean {
  const physical = context.facilityWorkstationPids?.get(facility.id);
  return (worker.facilityId === undefined || worker.facilityId === facility.id)
    && (physical ? physical.has(worker.workplacePid ?? -1) : worker.workplacePid === facility.anchorPid);
}

function workerRate(state: SettlementStateV1, facility: SettlementFacilityV1,
  job: SettlementCommissionV1, context: ProductionContext): number | null {
  const cap = facilityWorkerCap(facility, context);
  if (job.workerIds.length > cap) return null;
  const workers = job.workerIds.map((id) => state.residents.find((resident) => resident.id === id));
  if (workers.length === 0 || workers.some((worker) => !worker)) return null;
  if (workers.some((worker) => !workerUsesFacility(worker!, facility, context))) return null;
  const recipe = PRODUCTION_RECIPE_BY_ID.get(job.recipeId);
  if (!recipe || legacyProfessionSpecialty(workers[0]!.profession) !== recipe.profession
    || !leadHasSpecialty(workers[0]!, recipe)) return null;
  // Every compatible worker adds full personal speed, scaled by their own meal
  // happiness. An untrained assistant is still helpful at half Apprentice
  // speed; an unfed assistant contributes nothing until they eat again.
  let total = Math.floor(RESIDENT_RATE_PERMILLE[workers[0]!.rank] * happinessScale(workers[0]!) / 1000);
  for (let i = 1; i < workers.length; i++) {
    if (!residentFed(state, workers[i]!)) continue;
    const base = legacyProfessionSpecialty(workers[i]!.profession) === recipe.profession
      ? RESIDENT_RATE_PERMILLE[workers[i]!.rank] : 500;
    total += Math.floor(base * happinessScale(workers[i]!) / 1000);
  }
  return total;
}

/** Pure offline catch-up. Input is never mutated. Elapsed time splits at
 * fixed night boundaries: work earned before each boundary completes first,
 * then the town eats, then the recorded fed status gates the next segment. */
export function advanceSettlementProduction(input: SettlementStateV1, nowMs: number,
  context: ProductionContext = {}): SettlementStateV1 {
  const state = cloneSettlementState(input);
  const now = Number.isFinite(nowMs) ? Math.max(0, Math.floor(nowMs)) : state.updatedAt;
  state.lastMealNight ??= settlementNightIndex(state.updatedAt);
  for (let night = state.lastMealNight + 1; night * SETTLEMENT_NIGHT_MS <= now; night++) {
    // The pantry plateau: with nothing to eat and nobody fed, no future night
    // can differ from this one. Jump to the present instead of replaying it.
    if (settlementRationCount(state) === 0
      && state.residents.every((resident) => !residentFed(state, resident))) {
      state.lastMealNight = settlementNightIndex(now);
      break;
    }
    integrateSettlementProduction(state, night * SETTLEMENT_NIGHT_MS, context);
    resolveSettlementNightMeal(state, night);
    state.lastMealNight = night;
  }
  integrateSettlementProduction(state, now, context);
  resolveBankInterest(state, now);
  return state;
}

/** One constant-fed-status segment. Mutates the already-cloned state. */
function integrateSettlementProduction(state: SettlementStateV1, now: number,
  context: ProductionContext): void {
  const facilities = state.facilities.slice().sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  for (const facility of facilities) {
    const since = Math.max(0, now - Math.max(0, Math.floor(facility.lastEvaluatedAt)));
    facility.lastEvaluatedAt = now;
    if (context.activeFacilityIds && !context.activeFacilityIds.has(facility.id)) {
      if (facility.queue[0]) facility.queue[0].pauseReason = "FACILITY_INVALID";
      facility.rateRemainder = 0;
      continue;
    }
    startTargetIfPossible(state, facility, context);
    if (facility.queue.length === 0) { facility.rateRemainder = 0; continue; }
    if (!facility.target) {
      // Finite projects occupy real parallel lanes. Every job receives the
      // same elapsed interval with its own disjoint crew and fixed-point carry.
      const completed = new Set<string>();
      for (const job of facility.queue) {
        const lead = state.residents.find((resident) => resident.id === job.workerIds[0]);
        if (lead && !residentFed(state, lead)) {
          job.pauseReason = "WORKER_UNFED";
          job.rateRemainder = 0;
          continue;
        }
        const laneRate = workerRate(state, facility, job, context);
        if (laneRate === null) { job.pauseReason = "WORKER_UNAVAILABLE"; continue; }
        delete job.pauseReason;
        const numerator = since * laneRate + Math.max(0, Math.floor(job.rateRemainder ?? 0));
        const available = Math.floor(numerator / RATE_SCALE);
        job.rateRemainder = numerator - available * RATE_SCALE;
        job.workDoneMs += Math.min(Math.max(0, job.workRequiredMs - job.workDoneMs), available);
        if (job.workDoneMs < job.workRequiredMs) continue;
        const recipe = PRODUCTION_RECIPE_BY_ID.get(job.recipeId);
        if (!recipe) { job.pauseReason = "FACILITY_INVALID"; continue; }
        const batch = effectiveOutputs(state, recipe);
        if (!outputsFit(state, batch)) {
          job.pauseReason = "STORAGE_FULL";
          job.rateRemainder = 0;
          continue;
        }
        const outputs = addOutputs(state, job, recipe, batch);
        awardCommissionXp(state, job, recipe);
        state.completedCommissions = Math.min(
          SETTLEMENT_ACCOMPLISHMENT_MAX, state.completedCommissions + 1);
        completed.add(job.id);
        state.events.push({ id: `complete:${job.id}`, kind: "commission-complete",
          facilityId: facility.id, recipeId: recipe.id, outputs });
      }
      facility.queue = facility.queue.filter((job) => !completed.has(job.id));
      if (state.events.length > SETTLEMENT_EVENT_MAX) {
        state.events.splice(0, state.events.length - SETTLEMENT_EVENT_MAX);
      }
      facility.rateRemainder = 0;
      continue;
    }
    const targetLead = state.residents.find((resident) => resident.id === facility.queue[0]?.workerIds[0]);
    if (targetLead && !residentFed(state, targetLead)) {
      facility.queue[0].pauseReason = "WORKER_UNFED";
      facility.rateRemainder = 0;
      continue;
    }
    const rate = workerRate(state, facility, facility.queue[0], context);
    if (rate === null) {
      facility.queue[0].pauseReason = "WORKER_UNAVAILABLE";
      facility.rateRemainder = 0;
      continue;
    }
    let numerator = since * rate + Math.max(0, Math.floor(facility.rateRemainder));
    let availableWork = Math.floor(numerator / RATE_SCALE);
    facility.rateRemainder = numerator - availableWork * RATE_SCALE;

    while (facility.queue.length > 0) {
      const job = facility.queue[0];
      const recipe = PRODUCTION_RECIPE_BY_ID.get(job.recipeId);
      if (!recipe) { job.pauseReason = "FACILITY_INVALID"; facility.rateRemainder = 0; break; }
      delete job.pauseReason;
      const remaining = Math.max(0, job.workRequiredMs - job.workDoneMs);
      const applied = Math.min(remaining, availableWork);
      job.workDoneMs += applied;
      availableWork -= applied;
      if (job.workDoneMs < job.workRequiredMs) break;
      const batch = effectiveOutputs(state, recipe);
      if (!outputsFit(state, batch)) {
        job.pauseReason = "STORAGE_FULL";
        // Work cannot bank behind a blocked output. Time after the block is idle.
        facility.rateRemainder = 0;
        break;
      }
      const outputs = addOutputs(state, job, recipe, batch);
      awardCommissionXp(state, job, recipe);
      state.completedCommissions = Math.min(
        SETTLEMENT_ACCOMPLISHMENT_MAX, state.completedCommissions + 1);
      facility.queue.shift();
      state.events.push({
        id: `complete:${job.id}`,
        kind: "commission-complete",
        facilityId: facility.id,
        recipeId: recipe.id,
        outputs,
      });
      if (state.events.length > SETTLEMENT_EVENT_MAX) {
        state.events.splice(0, state.events.length - SETTLEMENT_EVENT_MAX);
      }
      startTargetIfPossible(state, facility, context);
      if (facility.queue.length === 0) { facility.rateRemainder = 0; break; }
      if (availableWork <= 0) break;
      // Queue validation guarantees the next job uses compatible assigned
      // workers, but its rank can change the rate. Stop at the job boundary;
      // the next evaluation begins from this authoritative timestamp rather
      // than misapplying the previous worker's rate.
      const nextRate = workerRate(state, facility, facility.queue[0], context);
      if (nextRate !== rate) { facility.rateRemainder = 0; break; }
    }
  }
  state.updatedAt = now;
}

/** Escrow inputs and append a finite job. Caller should advance to `nowMs`
 * first; this function does so defensively before validating the new queue. */
export function queueSettlementCommission(input: SettlementStateV1, facilityId: string,
  recipeId: string, workerIds: readonly string[], nowMs: number,
  context: ProductionContext = {}): QueueCommissionResult {
  const state = advanceSettlementProduction(input, nowMs, context);
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  const recipe = PRODUCTION_RECIPE_BY_ID.get(recipeId);
  if (!facility) return { ok: false, state, err: "FACILITY NOT FOUND" };
  if (!recipe) return { ok: false, state, err: "RECIPE NOT FOUND" };
  if (context.activeFacilityIds && !context.activeFacilityIds.has(facility.id)) {
    return { ok: false, state, err: "FACILITY ROOM IS INVALID" };
  }
  if (!recipeFitsBuilding(recipe, facility)) {
    return { ok: false, state, err: "RECIPE DOES NOT MATCH BUILDING" };
  }
  const capacity = settlementFacilityCapacity(facility);
  if (new Set(recipe.inputs.map((stack) => stack.id)).size > capacity.inputSlots
    || new Set(recipe.outputs.map((stack) => stack.id)).size > capacity.outputSlots) {
    return { ok: false, state, err: "BUILDING BUFFERS ARE TOO SMALL" };
  }
  if (facility.queue.length >= capacity.parallelJobs) {
    return { ok: false, state, err: "BUILDING WORK LANES ARE FULL" };
  }
  const uniqueWorkers = [...new Set(workerIds)];
  if (uniqueWorkers.length > facilityWorkerCap(facility, context)) {
    return { ok: false, state, err: "FACILITY NEEDS MORE WORKSTATIONS" };
  }
  const workers = uniqueWorkers.map((id) => state.residents.find((resident) => resident.id === id));
  if (workers.length === 0 || workers.some((worker) => !worker
    || !workerUsesFacility(worker, facility, context))) {
    return { ok: false, state, err: "ASSIGNED WORKER IS UNAVAILABLE" };
  }
  if (legacyProfessionSpecialty(workers[0]!.profession) !== recipe.profession) {
    return { ok: false, state, err: "LEAD WORKER DOES NOT KNOW THIS RECIPE" };
  }
  if (workers[0]!.rank < recipe.minRank) return { ok: false, state, err: "WORKER RANK TOO LOW" };
  if (!leadHasSpecialty(workers[0]!, recipe)) {
    return { ok: false, state, err: `LEAD NEEDS THE ${recipe.specialty!.toUpperCase()} SPECIALTY` };
  }
  const busyWorkers = new Set(facility.queue.flatMap((job) => job.workerIds));
  if (uniqueWorkers.some((id) => busyWorkers.has(id))) {
    return { ok: false, state, err: "A WORKER ALREADY OCCUPIES ANOTHER LANE" };
  }
  if (new Set([...busyWorkers, ...uniqueWorkers]).size > facilityWorkerCap(facility, context)) {
    return { ok: false, state, err: "BUILDING NEEDS MORE WORKSTATIONS" };
  }
  const id = appendCommission(state, facility, recipe, uniqueWorkers, "lowest-first");
  if (!id) return { ok: false, state, err: "MISSING SETTLEMENT INPUTS" };
  facility.lastEvaluatedAt = Math.max(0, Math.floor(nowMs));
  facility.rateRemainder = 0;
  state.updatedAt = facility.lastEvaluatedAt;
  return { ok: true, state, commissionId: id };
}

/** Declare or clear a common-good target. Work begins lazily and repeats only
 * until this quantity is present; no timer tick or repeated player click. */
export function setSettlementTarget(input: SettlementStateV1, facilityId: string,
  recipeId: string | null, targetQuantity: number, workerIds: readonly string[], nowMs: number,
  context: ProductionContext = {}): SettlementProgressionResult {
  const state = advanceSettlementProduction(input, nowMs, context);
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  if (!facility) return { ok: false, state, err: "FACILITY NOT FOUND" };
  if (recipeId === null || targetQuantity === 0) {
    delete facility.target;
    state.updatedAt = Math.max(0, Math.floor(nowMs));
    return { ok: true, state };
  }
  const recipe = PRODUCTION_RECIPE_BY_ID.get(recipeId);
  const workers = [...new Set(workerIds)].map((id) => state.residents.find((resident) => resident.id === id));
  if (!recipe?.targetable || !recipeFitsBuilding(recipe, facility)) {
    return { ok: false, state, err: "RECIPE CANNOT BE TARGETED HERE" };
  }
  if (!Number.isInteger(targetQuantity) || targetQuantity < 1 || targetQuantity > 100_000) {
    return { ok: false, state, err: "INVALID TARGET QUANTITY" };
  }
  const output = recipe.outputs[0]!;
  const bufferMax = settlementFacilityCapacity(facility).outputSlots * (ITEMS[output.id]?.stack ?? 1);
  if (targetQuantity > bufferMax) {
    return { ok: false, state, err: `BUILDING OUTPUT CAPACITY IS ${bufferMax}` };
  }
  if (workers.length < 1 || workers.some((worker) => !worker || !workerUsesFacility(worker, facility, context))
    || legacyProfessionSpecialty(workers[0]!.profession) !== recipe.profession
    || workers[0]!.rank < recipe.minRank
    || !leadHasSpecialty(workers[0]!, recipe)) {
    return { ok: false, state, err: "TARGET NEEDS A QUALIFIED LEAD WORKER" };
  }
  facility.target = {
    recipeId, targetQuantity, workerIds: workers.map((worker) => worker!.id), qualityPolicy: "lowest-first",
  };
  facility.lastEvaluatedAt = Math.max(0, Math.floor(nowMs));
  facility.rateRemainder = 0;
  state.updatedAt = facility.lastEvaluatedAt;
  return { ok: true, state };
}

export interface SettlementProgressionResult {
  ok: boolean;
  state: SettlementStateV1;
  err?: string;
}

/** Consume stored expedition goods and install the next finite facility grade.
 * The service validates its visible furniture prerequisite before calling. */
export function upgradeSettlementFacility(input: SettlementStateV1, facilityId: string,
  nowMs: number, context: ProductionContext = {}): SettlementProgressionResult {
  const state = advanceSettlementProduction(input, nowMs, context);
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  if (!facility) return { ok: false, state, err: "FACILITY NOT FOUND" };
  if (context.activeFacilityIds && !context.activeFacilityIds.has(facility.id)) {
    return { ok: false, state, err: "FACILITY ROOM IS INVALID" };
  }
  const upgrade = facilityUpgradeAfter(facility.grade);
  if (!upgrade) return { ok: false, state, err: "HIGHER FACILITY GRADES ARE NOT AVAILABLE YET" };
  if (!hasStacks(state.storage, upgrade.costs)) {
    return { ok: false, state, err: "MISSING SETTLEMENT UPGRADE MATERIALS" };
  }
  for (const cost of upgrade.costs) takeQuality(state, cost, "lowest-first");
  facility.grade = upgrade.toGrade;
  facility.lastEvaluatedAt = Math.max(0, Math.floor(nowMs));
  facility.rateRemainder = 0;
  state.updatedAt = facility.lastEvaluatedAt;
  return { ok: true, state };
}

/** Claim an earned rank at an appropriate workplace. Work is settled first so
 * the old and new throughput rates have an exact authoritative boundary.
 * `specialty` optionally chooses or changes the resident's specialty at the
 * same moment — the §3.3 "strength, not a trap" rule. */
export function promoteSettlementResident(input: SettlementStateV1, residentId: string,
  facilityId: string, nowMs: number, context: ProductionContext = {},
  specialty?: string | null): SettlementProgressionResult {
  const state = advanceSettlementProduction(input, nowMs, context);
  const resident = state.residents.find((candidate) => candidate.id === residentId);
  const facility = state.facilities.find((candidate) => candidate.id === facilityId);
  if (!resident) return { ok: false, state, err: "RESIDENT NOT FOUND" };
  if (!facility) return { ok: false, state, err: "FACILITY NOT FOUND" };
  if (resident.rank >= SETTLEMENT_PROGRESSION_MAX) {
    return { ok: false, state, err: "HIGHER RESIDENT RANKS ARE NOT AVAILABLE YET" };
  }
  if (context.activeFacilityIds && !context.activeFacilityIds.has(facility.id)) {
    return { ok: false, state, err: "FACILITY ROOM IS INVALID" };
  }
  const target = (resident.rank + 1) as SettlementRank;
  if (!workerUsesFacility(resident, facility, context)) {
    return { ok: false, state, err: "RESIDENT IS NOT ASSIGNED TO THIS FACILITY" };
  }
  if (resident.xp < RESIDENT_RANK_XP[target]) return { ok: false, state, err: "RESIDENT NEEDS MORE WORK EXPERIENCE" };
  if (specialty != null) {
    const options = SETTLEMENT_SPECIALTIES[legacyProfessionSpecialty(resident.profession) ?? ""];
    if (!options?.includes(specialty)) return { ok: false, state, err: "INVALID SPECIALTY CHOICE" };
    resident.specialty = specialty;
  }
  resident.rank = target;
  facility.lastEvaluatedAt = Math.max(0, Math.floor(nowMs));
  facility.rateRemainder = 0;
  state.updatedAt = facility.lastEvaluatedAt;
  return { ok: true, state };
}

/** Catalog integrity check useful to validation/tests. */
export function productionRecipeIssues(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const recipe of PRODUCTION_RECIPES) {
    if (ids.has(recipe.id)) issues.push(`duplicate recipe ${recipe.id}`);
    ids.add(recipe.id);
    if (recipe.workMs <= 0 || !Number.isInteger(recipe.workMs)) issues.push(`${recipe.id}: bad workMs`);
    if (recipe.xp <= 0 || !Number.isInteger(recipe.xp)) issues.push(`${recipe.id}: bad xp`);
    if (recipe.combine && recipe.inputs.reduce((sum, stack) => sum + stack.n, 0) !== 2) {
      issues.push(`${recipe.id}: combine recipes take exactly two input units`);
    }
    if (recipe.rework && !recipe.combine) issues.push(`${recipe.id}: rework implies combine`);
    for (const stack of [...recipe.inputs, ...recipe.outputs]) {
      if (!ITEMS[stack.id]) issues.push(`${recipe.id}: unknown item ${stack.id}`);
      if (!Number.isInteger(stack.n) || stack.n <= 0) issues.push(`${recipe.id}: bad quantity ${stack.id}`);
    }
  }
  for (const recipe of PRODUCTION_RECIPES) {
    if (!recipe.variants.every((variant) => SETTLEMENT_BUILDING_DEFS[variant].family === recipe.family)) {
      issues.push(`${recipe.id}: family/variant mismatch`);
    }
    if (!recipe.variants.every((variant) => settlementVariantAcceptsProfession(variant, recipe.profession))) {
      issues.push(`${recipe.id}: ${recipe.profession} cannot work in ${recipe.variants.join(",")}`);
    }
  }
  return issues;
}

/** Defensive wire guard for the server-owned snapshot. Persistence performs a
 * stricter validation on the server; this keeps malformed JSON from reaching
 * client UI code or being treated as authoritative progress. */
export function validateSettlementStateWire(raw: unknown, beaconId?: string): SettlementStateV1 | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const applicantRows = o.applicants === undefined ? [] : o.applicants;
  const applicantEpoch = o.applicantEpoch === undefined ? 0 : o.applicantEpoch;
  const stage = o.stage === undefined ? 0 : o.stage;
  const completedCommissions = o.completedCommissions === undefined ? 0 : o.completedCommissions;
  if (o.v !== SETTLEMENT_STATE_VERSION || typeof o.beaconId !== "string"
    || (beaconId !== undefined && o.beaconId !== beaconId)
    || !Array.isArray(o.residents) || o.residents.length > 32
    || !Array.isArray(o.facilities) || o.facilities.length > 32
    || typeof o.storage !== "object" || o.storage === null || Array.isArray(o.storage)
    || !Array.isArray(o.events) || o.events.length > SETTLEMENT_EVENT_MAX
    || !Array.isArray(applicantRows) || applicantRows.length > 3
    || !Number.isInteger(applicantEpoch) || (applicantEpoch as number) < 0
    || !Number.isInteger(stage) || (stage as number) < 0 || (stage as number) > 3
    || !Number.isInteger(completedCommissions) || (completedCommissions as number) < 0
    || (completedCommissions as number) > SETTLEMENT_ACCOMPLISHMENT_MAX
    || !Number.isInteger(o.storageCapacity) || (o.storageCapacity as number) < 1
    || !(o.lastMealNight === undefined || (Number.isInteger(o.lastMealNight) && (o.lastMealNight as number) >= 0))
    || !(o.mealPolicy === undefined || (typeof o.mealPolicy === "string"
      && (SETTLEMENT_MEAL_POLICIES as readonly string[]).includes(o.mealPolicy)))
    || !(o.storefrontPurchases === undefined || (Array.isArray(o.storefrontPurchases)
      && o.storefrontPurchases.length <= 64
      && o.storefrontPurchases.every((row) => typeof row === "string" && row.length <= 240)))
    || !(o.bank === undefined || (typeof o.bank === "object" && o.bank !== null
      && Number.isInteger((o.bank as Record<string, unknown>).credits)
      && ((o.bank as Record<string, unknown>).credits as number) >= 0
      && ((o.bank as Record<string, unknown>).credits as number) <= BANK_CREDITS_MAX
      && Number.isInteger((o.bank as Record<string, unknown>).lastInterestDay)
      && ((o.bank as Record<string, unknown>).lastInterestDay as number) >= 0))
    || !Number.isInteger(o.updatedAt) || (o.updatedAt as number) < 0) return null;
  if (o.guildOrders !== undefined) {
    if (!Array.isArray(o.guildOrders) || o.guildOrders.length > 8) return null;
    for (const value of o.guildOrders) {
      if (typeof value !== "object" || value === null) return null;
      const order = value as Record<string, unknown>;
      if (typeof order.id !== "string" || order.id.length > 240
        || typeof order.itemId !== "string" || !ITEMS[order.itemId]
        || typeof order.minQuality !== "string"
        || !(SETTLEMENT_QUALITY_TIERS as readonly string[]).includes(order.minQuality)
        || !Number.isInteger(order.quantity) || (order.quantity as number) < 1
        || (order.quantity as number) > 100_000
        || !Number.isInteger(order.premiumPermille) || (order.premiumPermille as number) < 0
        || (order.premiumPermille as number) > 400
        || !Number.isInteger(order.contributed) || (order.contributed as number) < 0
        || (order.contributed as number) > (order.quantity as number)
        || !Array.isArray(order.escrow)) return null;
    }
  }
  const professions = new Set<string>([...SETTLEMENT_PROFESSIONS, ...LEGACY_SETTLEMENT_PROFESSIONS]);
  const traits = new Set<string>(SETTLEMENT_RESIDENT_TRAITS);
  const roomFunctions = new Set<string>([
    "dwelling", "commons", "mine", "farm", "salvage", "harvester", "workshop",
    "smithy", "kitchen", "exchange", "office", "storage",
  ]);
  for (const value of o.residents) {
    if (typeof value !== "object" || value === null) return null;
    const r = value as Record<string, unknown>;
    if (typeof r.id !== "string" || typeof r.name !== "string"
      || !(r.profession === null || (typeof r.profession === "string" && professions.has(r.profession)))
      || !Number.isInteger(r.rank) || (r.rank as number) < 1 || (r.rank as number) > 5
      || !Number.isInteger(r.xp) || (r.xp as number) < 0
      || !(r.homePid === null || Number.isInteger(r.homePid))
      || !(r.workplacePid === null || Number.isInteger(r.workplacePid))
      || !(r.facilityId === undefined || typeof r.facilityId === "string")
      || !(r.trait === undefined || (typeof r.trait === "string" && traits.has(r.trait)))
      || !(r.specialty === undefined || (typeof r.specialty === "string" && SETTLEMENT_SPECIALTY_TOKENS.has(r.specialty)))
      || !(r.fedForDay === undefined || (Number.isInteger(r.fedForDay) && (r.fedForDay as number) >= 0))
      || !(r.mealHappinessPermille === undefined || (Number.isInteger(r.mealHappinessPermille)
        && (r.mealHappinessPermille as number) >= 0
        && (r.mealHappinessPermille as number) <= MEAL_HAPPINESS_CAP_PERMILLE))) return null;
  }
  for (const [id, n] of Object.entries(o.storage as Record<string, unknown>)) {
    if (!ITEMS[id] || !Number.isInteger(n) || (n as number) < 1) return null;
  }
  for (const value of o.facilities) {
    if (typeof value !== "object" || value === null) return null;
    const f = value as Record<string, unknown>;
    if (typeof f.id !== "string" || !Number.isInteger(f.anchorPid)
      || !(f.profession === undefined || (typeof f.profession === "string" && professions.has(f.profession)))
      || !(f.family === undefined || (typeof f.family === "string"
        && ["generator", "refiner", "crafter", "trader", "admin", "living"].includes(f.family)))
      || !(f.variant === undefined || (typeof f.variant === "string"
        && (SETTLEMENT_BUILDING_VARIANTS as readonly string[]).includes(f.variant)))
      || typeof f.roomFunction !== "string"
      || !Number.isInteger(f.grade) || (f.grade as number) < 1 || (f.grade as number) > 5
      || !Array.isArray(f.queue) || f.queue.length > 5
      || !Number.isInteger(f.lastEvaluatedAt) || !Number.isInteger(f.rateRemainder)
      || !Number.isInteger(f.nextCommission)) return null;
    if (f.target !== undefined) {
      if (typeof f.target !== "object" || f.target === null) return null;
      const target = f.target as Record<string, unknown>;
      if (typeof target.recipeId !== "string" || !PRODUCTION_RECIPE_BY_ID.get(target.recipeId)?.targetable
        || !Number.isInteger(target.targetQuantity) || (target.targetQuantity as number) < 1
        || (target.targetQuantity as number) > 100_000
        || !Array.isArray(target.workerIds) || target.workerIds.length < 1 || target.workerIds.length > 6
        || !target.workerIds.every((id) => typeof id === "string")
        || target.qualityPolicy !== "lowest-first") return null;
    }
    for (const valueJob of f.queue) {
      if (typeof valueJob !== "object" || valueJob === null) return null;
      const job = valueJob as Record<string, unknown>;
      if (typeof job.id !== "string" || typeof job.recipeId !== "string"
        || !PRODUCTION_RECIPE_BY_ID.has(job.recipeId)
        || !Array.isArray(job.workerIds) || !job.workerIds.every((id) => typeof id === "string")
        || !Array.isArray(job.escrowedInputs)
        || !Number.isInteger(job.workRequiredMs) || (job.workRequiredMs as number) < 1
        || !Number.isInteger(job.workDoneMs) || (job.workDoneMs as number) < 0
        || (job.workDoneMs as number) > (job.workRequiredMs as number)) return null;
    }
  }
  if (o.qualityStorage !== undefined) {
    if (typeof o.qualityStorage !== "object" || o.qualityStorage === null || Array.isArray(o.qualityStorage)) return null;
    for (const [id, rawCounts] of Object.entries(o.qualityStorage as Record<string, unknown>)) {
      if (!ITEMS[id] || !Array.isArray(rawCounts) || rawCounts.length !== 5
        || !rawCounts.every((n) => Number.isInteger(n) && (n as number) >= 0)) return null;
      const total = rawCounts.reduce<number>((sum, n) => sum + (n as number), 0);
      if (total !== ((o.storage as Record<string, number>)[id] ?? 0)) return null;
    }
  }
  for (const value of o.events) {
    if (typeof value !== "object" || value === null) return null;
    const event = value as Record<string, unknown>;
    if (typeof event.id !== "string" || event.kind !== "commission-complete"
      || typeof event.facilityId !== "string" || typeof event.recipeId !== "string"
      || !PRODUCTION_RECIPE_BY_ID.has(event.recipeId) || !Array.isArray(event.outputs)) return null;
  }
  for (const value of applicantRows) {
    if (typeof value !== "object" || value === null) return null;
    const applicant = value as Record<string, unknown>;
    if (typeof applicant.id !== "string" || typeof applicant.name !== "string"
      || typeof applicant.profession !== "string" || !professions.has(applicant.profession)
      || typeof applicant.trait !== "string" || !traits.has(applicant.trait)
      || !Number.isInteger(applicant.homePid) || !Number.isInteger(applicant.workplacePid)
      || typeof applicant.facilityId !== "string" || !Number.isInteger(applicant.facilityAnchorPid)
      || typeof applicant.roomFunction !== "string" || !roomFunctions.has(applicant.roomFunction)
      || !Number.isInteger(applicant.expiresAt) || (applicant.expiresAt as number) < 0) return null;
    if (applicant.placements !== undefined) {
      if (!Array.isArray(applicant.placements) || applicant.placements.length < 1
        || applicant.placements.length > 8) return null;
      for (const valuePlacement of applicant.placements) {
        if (typeof valuePlacement !== "object" || valuePlacement === null) return null;
        const placement = valuePlacement as Record<string, unknown>;
        if (!Number.isInteger(placement.homePid) || !Number.isInteger(placement.workplacePid)
          || typeof placement.facilityId !== "string" || !Number.isInteger(placement.facilityAnchorPid)
          || typeof placement.roomFunction !== "string" || !roomFunctions.has(placement.roomFunction)) return null;
      }
    }
  }
  return cloneSettlementState({
    ...o,
    applicantEpoch: applicantEpoch as number,
    applicants: applicantRows,
    stage: stage as SettlementStage,
    completedCommissions: completedCommissions as number,
  } as unknown as SettlementStateV1);
}
